import { useState } from "react";
import {
  Controller,
  type Control,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from "react-hook-form";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";

export interface FieldConfig<T extends FieldValues = FieldValues> {
  /** RHF field path — supports dot notation for nested fields (e.g., 'config.sessionTypes'). */
  name: Path<T>;
  label: string;
  type:
    | "text"
    | "textarea"
    | "number"
    | "autocomplete"
    | "select"
    | "multiselect"
    | "switch"
    | "date"
    | "datetime-local";
  required?: boolean;
  options?: { value: string; label: string }[];
  freeSolo?: boolean;
  normalize?: (value: string) => string;
  helperText?: string;
  disabled?: boolean;
  hidden?: boolean;
  rows?: number;
  placeholder?: string;
  /**
   * `data-testid` for E2E selectors. Applied to the native input for
   * text/textarea/number fields and to the select display element for
   * selects. Other field types ignore it (add wiring when first needed).
   */
  testId?: string;
}

interface SchemaFieldProps<T extends FieldValues> {
  config: FieldConfig<T>;
  control: Control<T>;
}

export function SchemaField<T extends FieldValues>({
  config,
  control,
}: SchemaFieldProps<T>) {
  if (config.hidden) return null;

  const {
    name,
    label,
    type,
    required,
    options,
    freeSolo,
    normalize,
    helperText,
    disabled,
    rows,
    placeholder,
    testId,
  } =
    config;

  if (type === "switch") {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                disabled={disabled}
              />
            }
            label={label}
          />
        )}
      />
    );
  }

  if (type === "date" || type === "datetime-local") {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <DatePickerField
            type={type}
            label={label}
            required={required}
            disabled={disabled}
            helperText={helperText}
            field={field}
            errorMessage={error?.message}
          />
        )}
      />
    );
  }

  if (type === "multiselect") {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            multiple
            options={options?.map((o) => o.value) ?? []}
            getOptionKey={(val) => val as string}
            getOptionLabel={(val) =>
              options?.find((o) => o.value === val)?.label ?? val
            }
            value={(field.value as string[]) ?? []}
            onChange={(_, newValue) => field.onChange(newValue)}
            onBlur={field.onBlur}
            disabled={disabled}
            renderTags={(value, getTagProps) =>
              value.map((val, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    label={options?.find((o) => o.value === val)?.label ?? val}
                    size="small"
                    {...tagProps}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                required={required}
                error={!!error}
                helperText={error?.message ?? helperText}
                placeholder={placeholder}
              />
            )}
          />
        )}
      />
    );
  }

  if (type === "autocomplete") {
    const optionValues = options?.map((o) => o.value) ?? [];
    const normalizeValue = (value: string | null) => {
      const trimmed = value?.trim() ?? "";
      if (!trimmed) return null;
      return normalize ? normalize(trimmed) : trimmed;
    };

    return (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            freeSolo={freeSolo}
            options={optionValues}
            getOptionKey={(val) => val as string}
            getOptionLabel={(val) =>
              options?.find((o) => o.value === val)?.label ?? val
            }
            isOptionEqualToValue={(option, value) => option === value}
            value={(field.value as string | null) ?? null}
            inputValue={(field.value as string | null) ?? ""}
            onChange={(_, newValue) => {
              field.onChange(normalizeValue(newValue as string | null));
            }}
            onInputChange={(_, newInputValue, reason) => {
              if (freeSolo && reason !== "reset") {
                field.onChange(normalizeValue(newInputValue));
              }
            }}
            onBlur={field.onBlur}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                required={required}
                error={!!error}
                helperText={error?.message ?? helperText}
                placeholder={placeholder}
              />
            )}
          />
        )}
      />
    );
  }

  // Text, textarea, number, select all render as TextField variants
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={field.value ?? ""}
          label={label}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          fullWidth
          error={!!error}
          helperText={error?.message ?? helperText}
          multiline={type === "textarea"}
          rows={type === "textarea" ? (rows ?? 3) : undefined}
          type={type === "number" ? "number" : "text"}
          select={type === "select"}
          onChange={
            // <input type="number"> emits a string from e.target.value
            // even when the user typed digits. Without coercion the
            // schema (z.number()) rejects with "expected number, received
            // string" any time the field is edited. Empty input becomes
            // null so a cleared field stays a valid optional. Non-numeric
            // garbage falls through as the raw string so the schema's
            // own error surfaces (rather than us silently masking).
            type === "number"
              ? (e) => {
                  const v = e.target.value;
                  if (v === "") return field.onChange(null);
                  const n = Number(v);
                  field.onChange(Number.isNaN(n) ? v : n);
                }
              : field.onChange
          }
          slotProps={{
            htmlInput: {
              ...(type === "number" ? { step: "any" } : undefined),
              ...(testId && type !== "select"
                ? { "data-testid": testId }
                : undefined),
            },
            ...(testId && type === "select"
              ? {
                  // @ts-expect-error MUI v7 slotProps.select doesn't widen to data-* attributes; forwarded at runtime.
                  select: { "data-testid": testId },
                }
              : undefined),
          }}
        >
          {type === "select" &&
            options?.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
        </TextField>
      )}
    />
  );
}

interface DatePickerFieldProps<T extends FieldValues> {
  type: "date" | "datetime-local";
  label: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  field: ControllerRenderProps<T, Path<T>>;
  errorMessage?: string;
}

/**
 * Clicking anywhere in the input opens the picker. The default MUI X
 * behavior is to only open via the calendar adornment button, which reads
 * as broken — organizers expect click-to-open on date inputs.
 */
function DatePickerField<T extends FieldValues>({
  type,
  label,
  required,
  disabled,
  helperText,
  field,
  errorMessage,
}: DatePickerFieldProps<T>) {
  const [open, setOpen] = useState(false);
  const format = type === "date" ? "YYYY-MM-DD" : "YYYY-MM-DD[T]HH:mm";
  const Picker = type === "date" ? DatePicker : DateTimePicker;

  // Wrap the Picker in a div carrying the testId. MUI X v9 uses sectioned
  // PickersTextField DOM by default (enableAccessibleFieldDOMStructure=false
  // was removed in v9); passing data-testid via slotProps fights the strict
  // PickersTextFieldProps type. Tests locate the inner sectioned input via
  // `getByTestId(...).locator('input')` and type digits matching the format.
  return (
    <div data-testid={`date-picker-${field.name}`}>
      <Picker
        label={label}
        format={format}
        value={field.value ? dayjs(field.value as string) : null}
        onChange={(d) =>
          field.onChange(d && d.isValid() ? d.format(format) : null)
        }
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        slotProps={{
          textField: {
            fullWidth: true,
            required,
            error: !!errorMessage,
            helperText: errorMessage ?? helperText,
            onBlur: field.onBlur,
            onClick: () => !disabled && setOpen(true),
            sx: {
              "& .MuiInputBase-root": {
                cursor: disabled ? "default" : "pointer",
              },
            },
          },
        }}
      />
    </div>
  );
}
